import { DropDownModel, GlobalStateModel } from "@/models";
import type { CertStatus } from "@/components/state-level-report/report-2-1/Reports2_1";
import { mapCertStatus } from "@/components/state-level-report/report-2-1/Reports2_1";
import { CertificationCycleStatusRow } from "@/models/file-submission.tsx";
import { parseCertificationMonth } from "@/utils/certifications";
import { fetchAuthenticatedData } from "@/queries/shared";

export const VIEW_TYPE_OPTIONS: DropDownModel[] = [
  { Value: "Agency", Text: "Agency" },
];

export type AgencySite = {
  siteId: string;
  siteName: string;
  cdsCode: string;
  providerCounty: string;
  openDate: string;
  closureDate: string | null;
};

export type AgencyCertification = {
  cycleId: string;
  submissionCycle: string;
  certificationStatus: CertStatus;
  certifierName: string;
  certifierEmail: string;
  certifierPhone: string;
  totalEnrollment: number;
};

export type AgencyDataResult = {
  leaId: string;
  agencyName: string;
  vendorNumber: string;
  sites: AgencySite[];
  certifications: AgencyCertification[];
};

type RawLeaPreschool = {
  PreschoolID: number;
  PreschoolName: string;
  PreschoolCDSCode: string;
  EffectiveStartDate: string;
  EffectiveEndDate: string | null;
  LeaCode: string | null;
  ProviderCounty: string | null;
};

export function buildEsiidPath(leaId: string): string {
  return `${leaId !== "0" ? "0-" : ""}${leaId}-`;
}

export async function fetchAgencyData(
  context: GlobalStateModel,
  esiid: string,
  leaCdsCode: string,
  agencyName: string,
  vendorNumber: string
): Promise<AgencyDataResult> {
  const esiidPath = buildEsiidPath(esiid);

  const [preschools, certifications] = await Promise.all([
    fetchAuthenticatedData<RawLeaPreschool[]>(
      context,
      `/json/entity/${esiidPath}/preschools?includeLEACode=true&includeProviderCounty=true`
    ),
    fetchAuthenticatedData<CertificationCycleStatusRow[]>(
      context,
      `/json/state/certification/agency_status/${esiidPath}`
    ),
  ]);

  return {
    leaId: leaCdsCode,
    agencyName,
    vendorNumber,
    sites: preschools.map((p) => ({
      siteId: p.PreschoolID.toString(),
      siteName: p.PreschoolName,
      cdsCode: `${p.LeaCode ?? ""}${p.PreschoolCDSCode}`,
      providerCounty: p.ProviderCounty ?? "",
      openDate: p.EffectiveStartDate,
      closureDate: p.EffectiveEndDate || null,
    })),
    certifications: certifications
      .filter((c) => c.Status !== "Upcoming")
      .sort((a, b) => b.ReportingCalendarId.localeCompare(a.ReportingCalendarId))
      .map((c) => ({
        cycleId: c.ReportingCalendarId,
        submissionCycle: parseCertificationMonth(c.ReportingCalendarId),
        certificationStatus: mapCertStatus(c.Status),
        certifierName: [c.CertifierFirstName, c.CertifierLastName]
          .filter(Boolean)
          .join(" "),
        certifierEmail: c.CertifierEmail ?? "",
        certifierPhone: c.CertifierPhoneNumber ?? "",
        totalEnrollment: c.TotalEnrollment,
      })),
  };
}
